import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { 
  LayoutDashboard, 
  KeyRound,
  FolderOpen, 
  Upload, 
  Calendar, 
  Users, 
  Bell, 
  LogOut, 
  Menu, 
  X,
  Scale,
  ChevronDown,
  Check,
  ClipboardList,
  FileBarChart,
  Mail,
  BarChart3,
  Clock,
  Gavel,
  Truck,
  Printer,
  Briefcase,
  FileText,
  MessageSquare,
  Search,
  ListOrdered,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { isScopedRole, isPresidentRole, ROLE_LABELS } from "./RoleScopeBanner";
import { ActiveRoleBadge } from "./RoleChooser";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

const Layout = () => {
  const { user, logout, openRoleChooser } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?unread_only=true`);
      setNotifications(response.data.slice(0, 5));
      setUnreadCount(response.data.length);
    } catch (error) {
      console.error("Failed to fetch notifications");
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all notifications as read");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isJudge = user?.role === "judge";
  const scoped = isScopedRole(user);
  const president = isPresidentRole(user);

  // Judge gets restricted navigation
  const allNavItems = isJudge ? [
    { to: "/judge-cases", icon: Briefcase, label: "Οι Υποθέσεις μου", pageId: "judge-cases" },
    { to: "/judge-reviews", icon: ClipboardList, label: "Οι Έλεγχοί μου", pageId: "judge-reviews" },
    { to: "/hearings", icon: Gavel, label: "Πινάκια", pageId: "hearings" },
    { to: "/dockets", icon: ListOrdered, label: "Εκθέματα SOLON", pageId: "dockets" },
    { to: "/bench-calendar", icon: Calendar, label: "Ημερολόγιο Έδρας", pageId: "bench-calendar" },
    { to: "/hearing-calculator", icon: Scale, label: "Υπολογισμός Δικασίμου", pageId: "hearing-calculator" },
    { to: "/calendar", icon: Calendar, label: "Ημερολόγιο", pageId: "calendar" },
    { to: "/kpold", icon: Scale, label: "ΚΠολΔ", pageId: "kpold" },
  ] : scoped ? [
    { to: "/hearings", icon: Gavel, label: president ? "Δικάσιμοι" : "Τα Πινάκια Μου", pageId: "hearings" },
    { to: "/dockets", icon: ListOrdered, label: "Εκθέματα SOLON", pageId: "dockets" },
    { to: "/my-compositions", icon: Users, label: president ? "Η Σύνθεσή Μου" : "Οι Συνθέσεις Μου", pageId: "my-compositions" },
    { to: "/bench-calendar", icon: Calendar, label: "Ημερολόγιο Έδρας", pageId: "bench-calendar" },
    { to: "/my-templates", icon: FileText, label: "Πρότυπα", pageId: "my-templates" },
  ] : [
    { to: "/", icon: LayoutDashboard, label: "Πίνακας Ελέγχου", pageId: "dashboard" },
    { to: "/today", icon: Clock, label: "Σήμερα", pageId: "today" },
    { to: "/cases", icon: FolderOpen, label: "Υποθέσεις", pageId: "cases" },
    { to: "/hearings", icon: Gavel, label: "Πινάκια", pageId: "hearings" },
    { to: "/dockets", icon: ListOrdered, label: "Εκθέματα SOLON", pageId: "dockets" },
    { to: "/bench-calendar", icon: Calendar, label: "Ημερολόγιο Έδρας", pageId: "bench-calendar" },
    { to: "/assignment-board", icon: ClipboardList, label: "Προσδιορισμός", pageId: "assignment-board" },
    { to: "/hearing-calculator", icon: Scale, label: "Υπολογισμός Δικασίμου", pageId: "hearing-calculator" },
    { to: "/kpold", icon: Scale, label: "ΚΠολΔ", pageId: "kpold" },
    { to: "/pretrial", icon: ClipboardList, label: "Προδικαστικός", pageId: "pretrial" },
    { to: "/filings-search", icon: Search, label: "Αναζήτηση Καταθέσεων", pageId: "filings-search" },
    { to: "/dispatch", icon: Truck, label: "Διακίνηση", pageId: "dispatch" },
    { to: "/printouts", icon: Printer, label: "Εκτυπώσεις", pageId: "printouts" },
    { to: "/exports-history", icon: FileText, label: "Ιστορικό Εξαγωγών", pageId: "exports-history" },
    { to: "/import", icon: Upload, label: "Εισαγωγή", pageId: "import" },
    { to: "/calendar", icon: Calendar, label: "Ημερολόγιο", pageId: "calendar" },
    { to: "/reports", icon: FileBarChart, label: "Αναφορές", pageId: "reports" },
    { to: "/statistics", icon: BarChart3, label: "Στατιστικά", pageId: "statistics" },
    { to: "/correspondence", icon: Mail, label: "Αλληλογραφία", pageId: "correspondence" },
    { to: "/messages", icon: MessageSquare, label: "Μηνύματα", pageId: "messages" },
  ];

  // Filter by allowed_pages (admin always sees all, null = all pages)
  const allowedPages = user?.allowed_pages;
  const navItems = (user?.role === "admin" || scoped || !allowedPages)
    ? allNavItems
    : allNavItems.filter(item => allowedPages.includes(item.pageId));

  if (user?.role === "admin") {
    navItems.push({ to: "/admin", icon: Users, label: "Διαχείριση Χρηστών", pageId: "admin" });
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0 lg:w-20"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              {sidebarOpen && (
                <div>
                  <h1
                    className="font-semibold text-slate-900 text-lg leading-tight"
                    data-testid="sidebar-court-branch"
                  >
                    {user?.court_branch || "Themis Pro"}
                  </h1>
                  <p className="text-xs text-slate-500">Γραμματεία Δικαστηρίου</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            <ul className="space-y-1 px-2">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `sidebar-item ${isActive ? "active" : ""}`
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {sidebarOpen && <span>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-slate-600 font-medium">
                  {user?.full_name?.charAt(0) || "U"}
                </span>
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {user?.full_name}
                  </p>
                  <p className="text-xs text-slate-500" data-testid="sidebar-role-label">
                    {user?.role === "admin" ? "Διαχειριστής" : 
                     user?.role === "judge" ? "Δικαστής" :
                     user?.role === "dispatch" ? "Προσωπικό Διακίνησης" :
                     user?.role === "katastima" ? "Κατάστημα" : ROLE_LABELS[user?.role] || "Χρήστης"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="glass-header sticky top-0 z-20 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden"
                data-testid="toggle-sidebar-btn"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:flex"
                data-testid="toggle-sidebar-desktop-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="notification-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-4 py-2 border-b">
                    <span className="font-semibold text-slate-900">Ειδοποιήσεις</span>
                    {unreadCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={markAllAsRead}
                        className="text-xs text-primary"
                        data-testid="mark-all-read-btn"
                      >
                        Σημείωση όλων
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="max-h-[300px]">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-slate-500 text-sm">
                        Δεν υπάρχουν νέες ειδοποιήσεις
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <DropdownMenuItem 
                          key={notification.id}
                          className="flex items-start gap-3 p-4 cursor-pointer"
                          onClick={() => markAsRead(notification.id)}
                          data-testid={`notification-${notification.id}`}
                        >
                          <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                            notification.notification_type === "urgent" ? "bg-red-500" :
                            notification.notification_type === "warning" ? "bg-amber-500" : "bg-blue-500"
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">{notification.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <div className="hidden sm:block mr-1">
                <ActiveRoleBadge user={user} onClick={openRoleChooser} />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.full_name?.charAt(0) || "U"}
                      </span>
                    </div>
                    <span className="hidden md:inline text-sm">{user?.full_name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                    <p className="text-xs text-slate-500">{user?.username}</p>
                    <div className="mt-1.5"><ActiveRoleBadge user={user} onClick={openRoleChooser} /></div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setPwOpen(true)} className="cursor-pointer" data-testid="change-password-btn">
                    <KeyRound className="w-4 h-4 mr-2" />Αλλαγή κωδικού
                  </DropdownMenuItem>
                  {(user?.roles || []).length > 1 && (
                    <DropdownMenuItem onClick={openRoleChooser} className="cursor-pointer" data-testid="switch-role-btn">
                      <Users className="w-4 h-4 mr-2" />Αλλαγή ιδιότητας
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Αποσύνδεση
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
};

export default Layout;
